import React, { useContext, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import RecordPaymentModal from './RecordPaymentModal';
import RegistrationDenyModal from './RegistrationDenyModal';
import RegistrationRefundModal from './RegistrationRefundModal';
import RegistrationChangeDivisionModal from './RegistrationChangeDivisionModal';
import RegistrationReinstateModal from './RegistrationReinstateModal';
import ViewRegistrationFormModal from '../Forms/ViewRegistrationFormModal';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { triggerNotification } from '../../../helpers/notificationHelper';
import RegistrationInvoiceCreateModal from './RegistrationInvoiceCreateModal';
import { REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM } from '../../../config/constants';
import RegistrationChangeOverrideEmailModal from './RegistrationChangeOverrideEmailModal';
import RegistrationOrderSummaryModal from './RegistrationOrderSummaryModal';
import SendOrderReceiptModal from './SendOrderReceiptModal';

const RegistrationListActionModals = ({
  modalState,
  onCloseModal,
  isDetailedView,
  onActionComplete
}) => {
  const { updatePurchasedRegFormSubmission } = useContext(RegistrationContext);
  const {
    fetchUpdatedAdminFinancialRegistration,
    fetchRegFormSubmissionRegistration
  } = useContext(RegistrationAdminContext);
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const [actionState, setActionState] = useState({
    isOpen: false,
    regFormSubmission: null,
    regFormSubmissions: [],
    action: '',
    isLoading: false,
    invoiceRemaining: false
  });
  const regAssociation = dashboardState.regAssociation.value;

  useEffect(() => {
    if (
      modalState.isOpen &&
      Object.values(REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM).includes(
        modalState.action
      ) &&
      modalState.item &&
      modalState.item[0]?.pkRegFormSubmission
    ) {
      setActionState({
        isOpen: true,
        regFormSubmission: modalState.item[0],
        regFormSubmissions: modalState.item,
        action: modalState.action,
        isLoading: false,
        invoiceRemaining: false
      });
    }
  }, [modalState.isOpen, modalState.action]);

  useEffect(() => {
    if (!actionState.isOpen) {
      onCloseModal();
    }
  }, [actionState.isOpen]);

  const closeActionMenu = () => {
    setActionState({
      ...actionState,
      isOpen: false
    });
  };

  const onActionCompleteCallback = (hasPriceChanges) => {
    if (onActionComplete) {
      onActionComplete(hasPriceChanges);
    }
    else if (isDetailedView) {
      closeActionMenu();
      onCloseModal();
      fetchRegFormSubmissionRegistration(
        actionState.regFormSubmission?.pkRegFormSubmission,
        {},
        null,
        (e) => {
          triggerNotification(e);
        }
      );
    }
    else if (actionState.regFormSubmission || hasPriceChanges) {
      fetchUpdatedAdminFinancialRegistration(
        actionState.regFormSubmission?.pkRegFormSubmission,
        null,
        true
      );
    }
  };

  return (
    <>
      <ViewRegistrationFormModal
        isDisabled
        isLoading={actionState.isLoading}
        isOpen={
          actionState.isOpen &&
          actionState.action === REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.view
        }
        isViewable
        isViewerAdmin
        onClose={closeActionMenu}
        onSubmit={(formData, additionalData) => {
          setActionState({
            ...actionState,
            isLoading: true
          });

          const hasRegPersonChanges = Object.entries(
            actionState.regFormSubmission.regPerson
          ).some(
            ([key, value]) =>
              typeof value !== 'object' &&
              actionState.regFormSubmission.regPerson[key] !==
                formData.regPerson[key]
          );
          updatePurchasedRegFormSubmission(
            actionState.regFormSubmission?.pkRegFormSubmission,
            {
              regFormSubmissionAnswers: formData.regFormSubmissionAnswers,
              regPerson: hasRegPersonChanges ? formData.regPerson : null,
              divisionRoles: formData.divisionRoles,
              financialAssistanceId: formData.isFinancialAssistanceEnabled
                ? formData.financialAssistanceId
                : null,
              doNotChargeForChanges: additionalData?.doNotChargeForChanges,
              regFormSubmissionExternalIds:
                formData.regFormSubmissionExternalIds,
              regFormSubmissionPersonContacts:
                formData.regFormSubmissionPersonContacts
            },
            () => {
              triggerNotification(
                'Form submission updated!',
                'Success',
                'green'
              );

              onActionCompleteCallback(additionalData);

              if (
                additionalData?.increaseCost >= 10 &&
                additionalData?.paymentMethod?.toLowerCase() === 'invoice'
              ) {
                setActionState({
                  ...actionState,
                  invoiceRemaining: true,
                  isLoading: false,
                  action: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.invoice
                });
              }
              else {
                closeActionMenu();
              }
            },
            (e) => {
              triggerNotification(e);
              setActionState({
                ...actionState,
                isLoading: false
              });
            }
          );
        }}
        pkRegFormSubmission={actionState.regFormSubmission?.pkRegFormSubmission}
      />
      <RecordPaymentModal
        isAdminViewing
        isDetailedView={isDetailedView}
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.viewPayments
        }
        onActionSuccess={onActionCompleteCallback}
        onClose={closeActionMenu}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationDenyModal
        isOpen={
          actionState.isOpen &&
          actionState.action === REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.deny
        }
        onClose={closeActionMenu}
        onRefresh={onActionCompleteCallback}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationReinstateModal
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.reinstate
        }
        onClose={closeActionMenu}
        onReinstate={onActionCompleteCallback}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationRefundModal
        isOpen={
          actionState.isOpen &&
          actionState.action === REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.refund
        }
        onClose={closeActionMenu}
        onRefresh={onActionCompleteCallback}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationChangeDivisionModal
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.changeDivision
        }
        onClose={closeActionMenu}
        onRefresh={onActionCompleteCallback}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationChangeOverrideEmailModal
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.changeOverrideEmail
        }
        onClose={closeActionMenu}
        onRefresh={onActionCompleteCallback}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationInvoiceCreateModal
        invoiceRemainingBalance={actionState.invoiceRemaining}
        isOpen={
          actionState.isOpen &&
          actionState.action === REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.invoice
        }
        onClose={closeActionMenu}
        onInvoice={onActionCompleteCallback}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />
      <RegistrationOrderSummaryModal
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.orderSummary
        }
        onClose={closeActionMenu}
        regAssociation={regAssociation}
        regFormSubmission={actionState.regFormSubmission}
      />

      <SendOrderReceiptModal
        isAdminViewing
        isOpen={
          actionState.isOpen &&
          actionState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.resendReceipt
        }
        onClose={closeActionMenu}
        pkRegCarts={Array.from(
          new Set(actionState.regFormSubmissions.map((s) => s.fkRegCart))
        )}
      />
    </>
  );
};

RegistrationListActionModals.propTypes = {
  isDetailedView: PropTypes.bool,
  modalState: PropTypes.object,
  onActionComplete: PropTypes.func,
  onCloseModal: PropTypes.func,
  regAssociation: PropTypes.object
};

export default RegistrationListActionModals;
